import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapPin, MapPinOff } from 'lucide-react';
import { RouteDay } from '../../services/userActivityTypes';
import { formatClock } from '../../services/userActivityStats';

// Tagesroute eines Nutzers auf der Karte.
//
// Duenner Container im Muster von crm/map/CrmMapView.tsx: hier steckt
// AUSSCHLIESSLICH die Leaflet-Instanz und das Zeichnen. Die Punkte kommen
// fertig sortiert aus services/userActivityStats.buildRouteDays.
//
// WICHTIG zur Datenherkunft: die Koordinaten stammen aus der PROJEKTADRESSE
// (data.metadata.globalData.locationCoordinates, gelesen ueber
// services/mapFeatures.projectToFeature), NICHT vom Geraet des Nutzers. Die
// Linie verbindet also die an diesem Tag geoeffneten Projekte in zeitlicher
// Reihenfolge — sie ist kein Bewegungsprofil.

/** Wie in crm/map/CrmMapView.tsx: keyless OSM, kein Drittanbieter-Proxy. */
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Deutschland-Ausschnitt, solange kein Punkt vorliegt. */
const START_MITTE: [number, number] = [51.1657, 10.4515];
const START_ZOOM = 6;

/** HTML-Text sicher einbetten. DivIcons und Popups sind rohes HTML. */
const esc = (s: unknown): string =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );

/** Nummerierter Marker 1..n. Erster und letzter Halt farblich abgesetzt. */
const nummerIcon = (nummer: number, istErster: boolean, istLetzter: boolean): L.DivIcon => {
    const farbe = istErster ? '#10b981' : istLetzter ? '#f43f5e' : '#3b82f6';
    return L.divIcon({
        // Leerer className: die Leaflet-Vorgabe malt sonst einen weissen Kasten.
        className: '',
        html:
            `<span style="display:flex;align-items:center;justify-content:center;` +
            `width:26px;height:26px;border-radius:9999px;background:${farbe};color:#fff;` +
            `font-size:11px;font-weight:800;border:2px solid #fff;` +
            `box-shadow:0 1px 4px rgba(15,23,42,0.45)">${nummer}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
    });
};

interface Props {
    routeDay: RouteDay;
    /** Anzeigename des Nutzers — steht ueber der Karte. */
    userLabel?: string;
    /** Tag in dd.MM.yyyy, wird bereits formatiert hereingereicht. */
    dayLabel?: string;
    /** Koordinaten werden gerade geholt — noch ist nichts „ohne Koordinaten". */
    coordinatesLoading?: boolean;
    /**
     * Koordinaten liessen sich nicht laden. Ohne diesen Hinweis wuerde die
     * Karte anschliessend JEDES Projekt als „ohne Koordinaten" ausweisen und
     * damit einen Datenmangel behaupten, den es nicht gibt.
     */
    coordinatesFailed?: boolean;
    /**
     * TEIL-Ausfall: die Projektadressen liegen vor, der Kundensitz als
     * Ersatzadresse liess sich nicht laden. Die betroffenen Projekte stehen
     * unten unter „ohne Koordinaten", obwohl darueber nichts bekannt ist.
     */
    coordinatesPartial?: boolean;
    /** Zahl der Projekte, die der ausgefallene Ersatzweg betrifft. */
    coordinatesPartialCount?: number;
}

export const UserActivityRouteMap: React.FC<Props> = ({
    routeDay,
    userLabel,
    dayLabel,
    coordinatesLoading,
    coordinatesFailed,
    coordinatesPartial,
    coordinatesPartialCount,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const layerRef = useRef<L.LayerGroup | null>(null);

    // ── Karte erzeugen (genau einmal) ───────────────────────────────────────
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const map = L.map(containerRef.current, {
            zoomControl: true,
            attributionControl: true,
        }).setView(START_MITTE, START_ZOOM);

        L.tileLayer(TILE_URL, { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map);
        layerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        // Leaflet misst beim Erzeugen die Containergroesse; steht die Ansicht
        // noch im Aufbau, ist sie 0 und die Karte bleibt grau.
        const t = window.setTimeout(() => map.invalidateSize(), 120);

        return () => {
            window.clearTimeout(t);
            map.off();
            map.remove();
            mapRef.current = null;
            layerRef.current = null;
        };
    }, []);

    // ── Punkte + Linie zeichnen ─────────────────────────────────────────────
    //
    // Abhaengig von `routeDay.points`, NICHT von `routeDay`: das umgebende
    // Objekt wurde bei jedem Render neu gebaut, der Effekt lief daraufhin
    // erneut und `fitBounds` warf den Ausschnitt zurueck — wer die Karte
    // verschoben oder gezoomt hatte, verlor das mit dem naechsten Render.
    // Die Punkte selbst sind memoisiert und wechseln nur mit echten Daten.
    const punkte = routeDay.points;
    useEffect(() => {
        const map = mapRef.current;
        const layer = layerRef.current;
        if (!map || !layer) return;

        layer.clearLayers();

        if (punkte.length === 0) {
            map.setView(START_MITTE, START_ZOOM);
            return;
        }

        const koordinaten: L.LatLngExpression[] = punkte.map((p) => [p.lat, p.lng]);

        if (punkte.length > 1) {
            L.polyline(koordinaten, {
                color: '#3b82f6',
                weight: 3,
                opacity: 0.75,
                dashArray: '8, 8',
            }).addTo(layer);
        }

        punkte.forEach((p, i) => {
            const marker = L.marker([p.lat, p.lng], {
                icon: nummerIcon(p.order, i === 0, i === punkte.length - 1),
                zIndexOffset: 400 + i,
            }).addTo(layer);

            marker.bindPopup(
                `<div class="font-sans text-xs min-w-[160px]">
                    <div class="font-bold text-slate-800 text-sm mb-1">${esc(p.projectName || 'Unbenanntes Projekt')}</div>
                    <div class="text-slate-500 mb-1">#${esc(p.projectNumber || '—')}</div>
                    <div class="text-slate-500">Halt ${p.order} · geöffnet ${esc(formatClock(p.occurredAt))} Uhr</div>
                </div>`,
            );
        });

        try {
            map.fitBounds(L.latLngBounds(koordinaten), { padding: [40, 40], maxZoom: 15 });
        } catch {
            /* fitBounds wirft bei entarteten Bounds — dann bleibt der Ausschnitt stehen. */
        }
    }, [punkte]);

    const ohneKoordinaten = routeDay.missingCoordinates;
    // Der Ersatzweg ist ausgefallen: die Liste unten stimmt dann nur noch fuer
    // die Projekte, deren Kundensitz tatsaechlich geprueft werden konnte.
    const teilAusfall = !!coordinatesPartial && !coordinatesFailed && !coordinatesLoading;
    const teilAusfallZahl = coordinatesPartialCount || 0;
    // Solange die Koordinaten fehlen oder unterwegs sind, ist „ohne
    // Koordinaten" keine Aussage ueber die Projekte, sondern ueber den
    // Ladezustand — die Liste bleibt dann aus.
    const zeigeOhneKoordinaten = !coordinatesLoading && !coordinatesFailed && ohneKoordinaten.length > 0;
    const leerText = coordinatesLoading
        ? 'Koordinaten werden geladen …'
        : coordinatesFailed
          ? 'Koordinaten nicht verfügbar.'
          : teilAusfall
            ? 'Keine verorteten Projekte — Ersatzadressen nicht geladen (siehe Hinweis unten).'
            : 'Keine verorteten Projekte an diesem Tag.';

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Tagesroute{userLabel ? ` — ${userLabel}` : ''}{dayLabel ? ` · ${dayLabel}` : ''}
                </h3>
                <span className="text-[11px] text-slate-400 text-right">
                    Projektadressen in zeitlicher Reihenfolge — kein Gerätestandort
                </span>
            </div>

            <div className="relative">
                <div ref={containerRef} className="w-full h-[420px] bg-slate-100" />
                {punkte.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/85 pointer-events-none">
                        <span className="text-slate-400 text-xs font-bold">{leerText}</span>
                    </div>
                )}
            </div>

            {coordinatesFailed && (
                <div className="px-5 py-3 border-t border-slate-100 bg-red-50/70">
                    <p className="text-[11px] font-bold text-red-700 flex items-start gap-1.5">
                        <MapPinOff className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                            Die Projektkoordinaten konnten nicht geladen werden — die Route ist deshalb leer. Das
                            heißt <strong>nicht</strong>, dass die Projekte keine Adresse haben. Über
                            „Aktualisieren" erneut versuchen.
                        </span>
                    </p>
                </div>
            )}

            {teilAusfall && (
                <div className="px-5 py-3 border-t border-slate-100 bg-orange-50/70">
                    <p className="text-[11px] font-bold text-orange-700 flex items-start gap-1.5">
                        <MapPinOff className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                            Die Projektadressen liegen vor, der <strong>Kundensitz als Ersatzadresse</strong> ließ sich
                            nicht laden
                            {teilAusfallZahl > 0 ? ` (betrifft ${teilAusfallZahl} Projekt(e))` : ''}. Diese Projekte
                            stehen unten unter „ohne Koordinaten", obwohl über ihre Lage nichts bekannt ist — sie können
                            beim nächsten Versuch durchaus erscheinen. Über „Aktualisieren" wiederholen.
                        </span>
                    </p>
                </div>
            )}

            {zeigeOhneKoordinaten && (
                <div className="px-5 py-3 border-t border-slate-100 bg-amber-50/60">
                    <p className="text-[11px] font-bold text-amber-700 flex items-center gap-1.5 mb-1.5">
                        <MapPinOff className="w-3.5 h-3.5" />
                        Ohne Koordinaten ({ohneKoordinaten.length}) — nicht auf der Karte
                        {teilAusfall && <span className="font-medium text-orange-700">· teils ungeprüft, siehe oben</span>}
                    </p>
                    <ul className="flex flex-wrap gap-1.5">
                        {ohneKoordinaten.map((m) => (
                            <li
                                key={m.projectId}
                                className="px-2 py-0.5 rounded-md bg-white border border-amber-200 text-[11px] font-bold text-amber-800"
                            >
                                #{m.projectNumber || m.projectId.slice(0, 8)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
